<template>
  <div class="guest-roster"> 
    <!-- Quick add -->
    <div class="quick-add">
      <standard-email-input
        v-model="invitee.email"
        :placeholder="placeholderText"
        class="quick-add__email-input"
        @invalid="inviteeEmailValid = false"
        @valid="inviteeEmailValid = true"
      />
      <div class="role-and-add">
        <dd-icon-button
          :class="AppSumoAssistant.getCreateAccountMembershipButtonMarkerClass()"
          :is-disabled="isSubmitting || inviteeInvalid"
          color="blue"
          size="small"
          icon="plus"
          @click="createAccountMembership()"
        />
      </div>
    </div>
    <!-- Roster -->
    <div v-for="person in company.listPeople.filter(p => p.accountRoleID === AccountRoleTypes.GUEST)" :key="person.id" class="roster">
      <avatar :person="person" :size="40" />
      <div class="person-info">
        <!-- Registered user -->
        <template v-if="person.hasRegistered">
          <div class="headline">
            <router-link :to="RouteAssistant.getGuestsDetailRoute(accountID, person.accountMembershipID)">
              <strong>{{ person.name }} <span v-if="$permissions.isLoggedInUser(person.id)">(You)</span></strong>
            </router-link>
          </div>
          <div class="subline">{{ person.email }}</div>
        </template>
        <!-- Unregistered user -->
        <template v-else>
          <div class="headline">
            <router-link :to="RouteAssistant.getGuestsDetailRoute(accountID, person.accountMembershipID)">
              <strong>{{ person.name }}</strong>
            </router-link>
          </div>
          <div class="subline">
            Has not registered &mdash;
            <span v-if="inviteSent(person.accountMembershipID)">
              Invite sent!
            </span>
            <span v-else class="just-a-link" @click="sendSignInEmail(person.accountMembershipID)" >
              Send invitation
            </span>
          </div>
        </template>
      </div>
      <div v-if="!$screen.isTabletOrMobile($mq)" class="actions">
        <div class="use-info">
          <router-link 
            :to="RouteAssistant.getGuestsDetailRoute(accountID, person.accountMembershipID)" 
            :class="{'has-memberships' : hasMemberships(person)}"
            class="just-a-link">
            {{ getProjectMailboxDisplay(person) }}
          </router-link>
          <div>{{ person.lastActivityDateDisplay }}</div>
        </div>
        <!-- Only render dropdown if rolled over. Otherwise, there is a noticeable client-side performance issue
             on larger accts with the dropdown renderings -->
        <div 
          v-if="!isMoreDropdownRolledOver(person.accountMembershipID)"
          class="more-button-ghost"
          @mouseover="onMoreDropdownRollover(person.accountMembershipID)"
        >
          <i class="ddi ddi-ellipsis" />
        </div>
        <more-button-dropdown v-else class="more-button">
          <template #options-list>
            <ul>
              <li v-if="$permissions.isLoggedInUser(person.id)" @click="RouteAssistant.goToProfile(accountID)">
                <i class="ddi ddi-profile" />
                <span>Edit Profile</span>
              </li>
              <li @click="RouteAssistant.goToGuestsDetail($router, accountID, person.accountMembershipID)">
                <i class="ddi ddi-edit" />
                <span>Edit User</span>
              </li>
              <li @click="sendSignInEmail(person.accountMembershipID)">
                <i class="ddi ddi-people" />
                <span> {{ getSendInviteText(person) }} </span>
              </li>
              <li @click="convertToNormalUser(person)">
                <i class="ddi ddi-multiple-actions-money" />
                <span>Convert to Normal user</span>
              </li>
              <li 
                v-if="$permissions.canDeleteAccountMembership(person.id, person.accountRoleID)"
                class="scary-list-option"
                @click="initiateDeletePerson(person)"
              >
                <i class="ddi ddi-trash" />
                <span>Delete User</span>
              </li>
            </ul>
          </template>
        </more-button-dropdown>
      </div>
    </div>
    <!-- Delete person modal -->
    <delete-person-modal 
      v-model:show-modal="showDeletePersonModal" 
      :person="personToDelete as unknown as IAccountMembershipsOutputDetail"
      @person-deleted="$emit('person-deleted')"
    />
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */
import DeletePersonModal from '../components/delete-person-modal.vue'
import StandardEmailInput from '@/components/global/inputs/standard-email-input.vue'

/* Helpers */

import RouteAssistant from '@/helpers/route-assistant'
import AppSumoAssistant from '@/helpers/appsumo-assistant'

/* Data types */
import { AccountRoles } from '@/types/defined-types'
import { ICompaniesInputCreateAccountMembership } from '@/api/types/request.types'
import {
  ICompanyRoster,
  IItemForSelection,
  IRosterPerson,
  IAccountMembershipsOutputDetail,
} from '@/api/types/response.types'

/* API */
import AccountMembershipsAPI from '@/api/modules/account-memberships.api'
import CompaniesAPI from '@/api/modules/companies.api'
import { AccountRoleTypes } from '@/types'

export default defineComponent({
  name: 'GuestRoster',

  components: {
    DeletePersonModal,
    StandardEmailInput,
  },

  props: {
    company: {
      type: Object as () => ICompanyRoster,
      required: true,
    },
  },

  emits: [
    'account-memberships-updated',
    'account-role-update-disallowed',
    'person-deleted',
  ],

  data() {
    return {
      AppSumoAssistant,
      AccountRoleTypes,
      isSubmitting: false,
      AccountRoles,
      RouteAssistant,
      sentInviteAccountMembershipIDs: [] as number[],
      invitee: {
        accountRoleID: 4,
        sendInvitation: true,
      } as ICompaniesInputCreateAccountMembership,
      inviteeEmailValid: false,

      /* Delete person modal data */
      personToDelete: {} as IRosterPerson,
      showDeletePersonModal: false,

      renderSelectAccountMembershipID: null as any,
      quickAddSelectRollover: false,

      renderDropdownAccountMembershipID: null as any,
    }
  },

  computed: {
    accountID(): number {
      return parseInt(this.$route.params.accountId, 10)
    },
    availableRoles(): IItemForSelection[] {
      return this.$permissions.availableRolesToAdmin
    },
    inviteeInvalid(): boolean {
      return !(this.invitee.accountRoleID && this.inviteeEmailValid)
    },
    placeholderText(): string {
      return 'Invite to ' + this.company.name + ' by email...'
    },
  },

  mounted() {
    this.resetInvitee()
  },

  methods: {
    resetInvitee() {
      this.invitee = {
        accountRoleID: 4,
        sendInvitation: true,
      } as ICompaniesInputCreateAccountMembership
    },
    onQuickAddSelectRollover() {
      this.quickAddSelectRollover = true
    },
    onSelectRollover(accountMembershipID: number) {
      this.renderSelectAccountMembershipID = accountMembershipID
    },
    isSelectRolledOver(accountMembershipID: number) {
      return (
        this.renderSelectAccountMembershipID &&
        this.renderSelectAccountMembershipID === accountMembershipID
      )
    },
    onMoreDropdownRollover(accountMembershipID: number) {
      this.renderDropdownAccountMembershipID = accountMembershipID
    },
    isMoreDropdownRolledOver(accountMembershipID: number) {
      return (
        this.renderDropdownAccountMembershipID &&
        this.renderDropdownAccountMembershipID === accountMembershipID
      )
    },
    hasMemberships(person: IRosterPerson): boolean {
      return !(person.projectCount === 0 && person.mailboxCount === 0)
    },
    inviteSent(accountMembershipID: number): boolean {
      return this.sentInviteAccountMembershipIDs.includes(accountMembershipID)
    },
    getSendInviteText(person: IRosterPerson): string {
      return person.hasRegistered ? 'Email Password Reset' : 'Email Invitation'
    },
    getProjectMailboxDisplay(person: IRosterPerson): string {
      let result: string = ''

      // Guest roles do not have access to mailbox
      const mailboxAcccess = person.accountRoleID !== 4

      if (!this.hasMemberships(person)) {
        return mailboxAcccess
          ? 'Add to projects & mailboxes'
          : 'Add to projects'
      }

      if (person.projectCount === 1) {
        result += '1 project'
      } else if (person.projectCount > 1) {
        result += person.projectCount + ' projects'
      }

      if (person.projectCount > 0 && person.mailboxCount > 0) {
        result += ' & '
      }
      if (person.mailboxCount === 1) {
        result += '1 mailbox'
      } else if (person.mailboxCount > 1) {
        result += person.mailboxCount + ' mailboxes'
      }

      return result
    },
    initiateDeletePerson(person: IRosterPerson) {
      this.personToDelete = person
      this.showDeletePersonModal = true
    },
    getAccountRoleName(accountRoleID: number) {
      return AccountRoles.find(a => a.id === accountRoleID).name
    },
    async convertToNormalUser(person: IRosterPerson) {
      this.$confirm('', {
        center: true,
        confirmButtonText: 'Yes, Convert',
        confirmButtonClass: 'el-button--strawberry',
        cancelButtonText: 'Cancel',
        cancelButtonClass: 'el-button--text',
        type: 'warning',
        title: `Convert "${person.name}" to Normal user?`,
      }).then(() => {
        AccountMembershipsAPI.put_UpdateRole(
          person.accountMembershipID,
          AccountRoleTypes.NORMAL,
        )
          .then(response => {
            person.accountRoleID = AccountRoleTypes.NORMAL
            this.$messaging.showSuccess(response.message)
          })
          .catch(error => {
            this.$error.emit(error, true)
            this.$emit('account-role-update-disallowed')
          })
      })
    },
    async sendSignInEmail(accountMembershipID: number) {
      try {
        const response = await AccountMembershipsAPI.get_SendSignInEmail(
          accountMembershipID,
        )
        this.sentInviteAccountMembershipIDs.push(accountMembershipID)
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      }
    },
    async createAccountMembership() {
      if (
        !(await AppSumoAssistant.isUnderAccountMembershipsLimit(
          this.$router,
          this.$permissions,
        ))
      ) {
        return
      }

      this.isSubmitting = true
      try {
        const response = await CompaniesAPI.post_CreateAccountMembership(
          this.company.id,
          this.invitee,
        )
        this.sentInviteAccountMembershipIDs.push(response.id)
        this.$messaging.showSuccess(response.message)
        this.resetInvitee()
        this.$emit('account-memberships-updated')
      } catch (error) {
        this.$error.emit(error, true)
      }
      this.isSubmitting = false
    },
  },
})
</script>

<style lang="scss">
.guest-roster {
  .select-ghost {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: $border--solid-light;
    background-color: var(--color-background-header);
    padding: 8px 11px 7px 12px;
    border-radius: 8px;
    width: 140px;
    cursor: pointer;

    @include media-breakpoint-down(sm) {
      width: 100%;
    }

    .ddi-caret-down {
      font-size: 12px;
      top: -1px;
      left: 1px;
    }

    &.disabled {
      cursor: not-allowed;
      background-color: var(--color-background-secondary);
      color: var(--color-text-third);
    }

    &.disabled-white {
      background-color: var(--color-background-header);
      color: var(--color-text-third);
    }
  }

  .more-button-ghost {
    font-size: 18px;
    color: var(--color-text-third);
    padding: 0 7px;
  }

  .roster {
    display: flex;
    align-items: center;
    padding: 14px 0;
    @include lettering--p;
    border-top: $border--solid-extra-light;

    .person-info {
      padding-left: 10px;
      line-height: 20px;

      .headline {
        margin-bottom: 1px;

        > a {
          color: var(--color-text-secondary);
        }
      }

      .subline {
        color: var(--color-text-third);
      }
    }

    .use-info {
      @include lettering--p-sm;
      text-align: right;
      padding-left: 10px;
      line-height: 20px;
    }

    .actions {
      display: flex;
      align-items: center;
      margin-left: auto;

      > * {
        margin-left: 12px;
      }

      .has-memberships {
        @include clickable-gray-color-states;
      }
    }
  }

  .account-roles-selector {
    width: 140px;

    @include media-breakpoint-down(sm) {
      width: 100%;
    }
  }

  .quick-add {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 0;
    @include lettering--p;

    @include media-breakpoint-down(sm) {
      flex-direction: column;
      align-items: flex-start;
    }

    &__email-input {
      width: 100%;

      @include media-breakpoint-up(md) {
        width: 360px;
      }
    }

    .role-and-add {
      display: flex;
      align-items: center;
      margin-right: 4px;

      @include media-breakpoint-down(sm) {
        justify-content: space-between;
        margin-top: 10px;
        width: 100%;
      }

      > *:last-child {
        margin-left: 16px;
      }
    }
  }
}
</style>
